import {
  minutes,
  money,
  percent,
  type AssignmentId,
  type ISODate,
  type ISODateTime,
  type Minutes,
  type Percent,
  type ResourceId,
  type TaskId,
  type TimesheetId,
} from '../domain/branded.js';
import type { Assignment } from '../domain/assignment.js';
import type { Project } from '../domain/project.js';
import type { Task, TaskProgressMode, TaskWorkflowStatus } from '../domain/task.js';
import { taskProgressMode } from '../domain/task.js';
import type { Timesheet, TimesheetEntry } from '../domain/timesheet.js';
import { applyApprovedTimesheets } from './cost-timesheet.js';
import { rollUpSummaryProgress, updateTaskProgress } from './task-progress.js';

/**
 * 逐日實際工時回報。
 *
 * 本產品沒有送審／核准流程（見 MS_PROJECT_PARITY_PLAN），因此這裡寫入的
 * 工時表一律直接是 `Approved`，才能被 `aggregateApprovedTimesheets()` 採計。
 * 每位資源固定一份工時表（id = `timesheet-<resourceId>`），
 * `periodStart`／`periodEnd` 隨 entries 的最早／最晚日期擴張；
 * entry id 為 `<assignmentId>@<date>`，讓「同一指派同一天」天然唯一、可 upsert。
 */

export interface ActualWorkEntryInput {
  readonly date: ISODate;
  readonly taskId: TaskId;
  readonly assignmentId: AssignmentId;
  readonly actualWork: Minutes;
  readonly overtimeWork?: Minutes;
  /** 回報的剩餘工時；省略代表未回報。 */
  readonly remainingWork?: Minutes;
  readonly notes?: string;
}

export interface RecordActualWorkInput {
  readonly resourceId: ResourceId;
  readonly entries: readonly ActualWorkEntryInput[];
  readonly reportedBy?: string;
  readonly reportedAt?: ISODateTime;
}

export type ProgressReportErrorCode =
  | 'RESOURCE_NOT_FOUND'
  | 'ASSIGNMENT_NOT_FOUND'
  | 'ASSIGNMENT_MISMATCH'
  | 'TASK_NOT_FOUND'
  | 'SUMMARY_TASK'
  | 'DUPLICATE_ENTRY'
  | 'INVALID_DATE'
  | 'INVALID_WORK'
  | 'INVALID_PERCENT'
  | 'PROGRESS_UPDATE_FAILED';

export interface ProgressReportError {
  readonly code: ProgressReportErrorCode;
  readonly message: string;
}

export type ProgressReportResult =
  | { readonly ok: true; readonly project: Project }
  | { readonly ok: false; readonly error: ProgressReportError };

const fail = (code: ProgressReportErrorCode, message: string): ProgressReportResult => ({
  ok: false,
  error: { code, message },
});

const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

const startOfDay = (date: ISODate): ISODateTime => `${date as string}T00:00:00` as ISODateTime;
const endOfDay = (date: ISODate): ISODateTime => `${date as string}T23:59:59` as ISODateTime;

const timesheetIdFor = (resourceId: ResourceId): TimesheetId =>
  `timesheet-${resourceId as string}` as TimesheetId;

const entryIdFor = (assignmentId: AssignmentId, date: ISODate): string =>
  `${assignmentId as string}@${date as string}`;

function isNonNegative(value: number | undefined): boolean {
  return value === undefined || (Number.isFinite(value) && value >= 0);
}

/** 依 pct 同步看板狀態：100 → done；有進度但仍標 todo → inProgress。 */
function workflowStatusForPercent(
  current: TaskWorkflowStatus | undefined,
  pct: number,
): TaskWorkflowStatus | undefined {
  if (pct >= 100) return 'done';
  if (pct > 0 && (current === undefined || current === 'todo')) return 'inProgress';
  if (pct <= 0 && current === 'done') return 'todo';
  return current;
}

function mapTask(project: Project, taskId: TaskId, patch: (task: Task) => Task): Project {
  return { ...project, tasks: project.tasks.map((task) => (task.id === taskId ? patch(task) : task)) };
}

/** manual 模式的剩餘工時：以指定進度換算，`work × (1 − pct)`。 */
function manualRemaining(assignment: Assignment, pct: number): Minutes {
  return minutes(Math.max(0, (assignment.work as number) * (1 - pct / 100)));
}

// ---------------------------------------------------------------------------
// 逐日實際工時
// ---------------------------------------------------------------------------

function upsertEntries(
  project: Project,
  input: RecordActualWorkInput,
): readonly Timesheet[] {
  const sheets = project.timesheets ?? [];
  const existing = sheets.find((sheet) => sheet.resourceId === input.resourceId);
  const byId = new Map<string, TimesheetEntry>(
    (existing?.entries ?? []).map((entry) => [entry.id, entry]),
  );

  for (const row of input.entries) {
    const id = entryIdFor(row.assignmentId, row.date);
    const notes = row.notes ?? '';
    const actual = row.actualWork as number;
    if (actual === 0 && notes === '' && row.remainingWork === undefined) {
      byId.delete(id);
      continue;
    }
    byId.set(id, {
      id,
      date: row.date,
      taskId: row.taskId,
      assignmentId: row.assignmentId,
      actualWork: row.actualWork,
      overtimeWork: row.overtimeWork ?? minutes(0),
      remainingWork: row.remainingWork ?? null,
      notes,
      ...(input.reportedBy === undefined ? {} : { reportedBy: input.reportedBy }),
      ...(input.reportedAt === undefined ? {} : { reportedAt: input.reportedAt }),
    });
  }

  const entries = [...byId.values()].sort((a, b) =>
    a.date < b.date ? -1 : a.date > b.date ? 1 : a.id < b.id ? -1 : a.id > b.id ? 1 : 0,
  );
  const first = entries[0];
  const last = entries[entries.length - 1];
  const sheet: Timesheet = {
    id: existing?.id ?? timesheetIdFor(input.resourceId),
    resourceId: input.resourceId,
    periodStart: first?.date ?? (input.entries[0]?.date ?? ('1970-01-01' as ISODate)),
    periodEnd: last?.date ?? (input.entries[0]?.date ?? ('1970-01-01' as ISODate)),
    entries,
    status: 'Approved',
    submittedAt: null,
    approvedAt: input.reportedAt ?? null,
    approvedBy: null,
    rejectionReason: null,
  };
  return existing === undefined
    ? [...sheets, sheet]
    : sheets.map((candidate) => (candidate.id === existing.id ? sheet : candidate));
}

/**
 * 寫入（upsert）一位資源的逐日實際工時，並依各任務的 `progressMode` 推導後續：
 * - `work`：走 `updateTaskProgress()`，由工時反推 percentComplete 與實際起迄日。
 * - `manual`：只更新指派的 actualWork／overtimeWork／actualCost，剩餘工時依指定進度換算，
 *   不動 percentComplete 與實際起迄日。
 *
 * 同一 (assignmentId, date) 會覆寫；`actualWork` 為 0 且無備註、未回報剩餘工時時移除該日。
 */
export function recordActualWork(project: Project, input: RecordActualWorkInput): ProgressReportResult {
  if (!project.resources.some((resource) => resource.id === input.resourceId)) {
    return fail('RESOURCE_NOT_FOUND', `Resource ${input.resourceId as string} does not exist.`);
  }

  const assignmentsById = new Map(project.assignments.map((assignment) => [assignment.id as string, assignment]));
  const tasksById = new Map(project.tasks.map((task) => [task.id as string, task]));
  const parentIds = new Set(project.tasks.map((task) => task.parentId).filter((id): id is TaskId => id !== null));
  const seen = new Set<string>();
  const touchedTaskIds = new Set<string>();

  for (const row of input.entries) {
    if (!DATE_PATTERN.test(row.date as string)) {
      return fail('INVALID_DATE', `Entry date ${row.date as string} must be formatted as YYYY-MM-DD.`);
    }
    const key = entryIdFor(row.assignmentId, row.date);
    if (seen.has(key)) {
      return fail('DUPLICATE_ENTRY', `Assignment ${row.assignmentId as string} was reported twice for ${row.date as string}.`);
    }
    seen.add(key);

    const assignment = assignmentsById.get(row.assignmentId as string);
    if (assignment === undefined) {
      return fail('ASSIGNMENT_NOT_FOUND', `Assignment ${row.assignmentId as string} does not exist.`);
    }
    if (assignment.resourceId !== input.resourceId || assignment.taskId !== row.taskId) {
      return fail('ASSIGNMENT_MISMATCH', `Assignment ${row.assignmentId as string} does not belong to the reported resource and task.`);
    }
    const task = tasksById.get(row.taskId as string);
    if (task === undefined) return fail('TASK_NOT_FOUND', `Task ${row.taskId as string} does not exist.`);
    if (task.isSummary || parentIds.has(task.id)) {
      return fail('SUMMARY_TASK', `Task ${row.taskId as string} is a summary task and cannot receive actual work.`);
    }

    const actual = row.actualWork as number;
    const overtime = (row.overtimeWork ?? minutes(0)) as number;
    const remaining = row.remainingWork as number | undefined;
    if (!isNonNegative(actual) || !isNonNegative(overtime) || !isNonNegative(remaining) || overtime > actual) {
      return fail('INVALID_WORK', `Entry for assignment ${row.assignmentId as string} on ${row.date as string} has invalid work values.`);
    }
    touchedTaskIds.add(row.taskId as string);
  }

  const timesheets = upsertEntries(project, input);
  // 工時表是實際工時的唯一真相：整批重新回填，而不是就地累加，
  // 這樣刪除某一天的回報也會正確地把總數往下修。
  const withTimesheets: Project = { ...project, timesheets };
  let next = applyApprovedTimesheets(withTimesheets, timesheets);

  const reportedAssignmentIds = new Set(
    timesheets.flatMap((sheet) => sheet.entries).map((entry) => entry.assignmentId as string),
  );
  // 這批回報把某個指派的紀錄刪光時，`applyApprovedTimesheets()` 沒有它的彙總，
  // 舊的實際工時／成本會殘留。工時表是唯一真相，所以這裡明確歸零。
  const clearedAssignmentIds = new Set(
    input.entries
      .map((row) => row.assignmentId as string)
      .filter((assignmentIdValue) => !reportedAssignmentIds.has(assignmentIdValue)),
  );
  const nextTasksById = new Map(next.tasks.map((task) => [task.id as string, task]));
  next = {
    ...next,
    assignments: next.assignments.map((assignment) => {
      const assignmentIdValue = assignment.id as string;
      const owner = nextTasksById.get(assignment.taskId as string);
      // manual 模式的剩餘工時不能用「work − actual」，改以指定進度換算。
      const manualPct = owner !== undefined && taskProgressMode(owner) === 'manual'
        ? (owner.percentComplete as number)
        : null;
      if (clearedAssignmentIds.has(assignmentIdValue)) {
        return {
          ...assignment,
          actualWork: minutes(0),
          overtimeWork: minutes(0),
          actualCost: money(0),
          remainingWork: manualPct === null ? assignment.work : manualRemaining(assignment, manualPct),
          timephased: null,
        };
      }
      if (!reportedAssignmentIds.has(assignmentIdValue) || manualPct === null) return assignment;
      return { ...assignment, remainingWork: manualRemaining(assignment, manualPct), timephased: null };
    }),
  };

  for (const taskIdValue of touchedTaskIds) {
    const task = nextTasksById.get(taskIdValue)!;
    if (taskProgressMode(task) === 'manual') continue;

    const taskAssignments = next.assignments.filter((assignment) => assignment.taskId === task.id);
    // 實際起迄日只能由「真的有投入工時」的日子決定：
    // 指派被刪掉後留下的孤兒 entry 不算，只為了備註或剩餘工時而保留的零工時日也不算。
    const liveAssignmentIds = new Set(taskAssignments.map((assignment) => assignment.id as string));
    const dates = timesheets
      .flatMap((sheet) => sheet.entries)
      .filter((entry) => entry.assignmentId !== null
        && liveAssignmentIds.has(entry.assignmentId as string)
        && ((entry.actualWork as number) > 0 || (entry.overtimeWork as number) > 0))
      .map((entry) => entry.date)
      .sort();
    const totalActual = taskAssignments.reduce((sum, assignment) => sum + (assignment.actualWork as number), 0);
    const totalRemaining = taskAssignments.reduce((sum, assignment) => sum + (assignment.remainingWork as number), 0);
    const completed = totalActual > 0 && totalRemaining === 0;
    const first = dates[0];
    const last = dates[dates.length - 1];

    const result = updateTaskProgress(next, {
      taskId: task.id,
      assignments: taskAssignments.map((assignment) => ({
        assignmentId: assignment.id,
        actualWork: assignment.actualWork,
        remainingWork: assignment.remainingWork,
        overtimeWork: assignment.overtimeWork ?? minutes(0),
      })),
      actualStart: totalActual > 0 ? (task.actualStart ?? (first === undefined ? null : startOfDay(first))) : null,
      actualFinish: completed ? (last === undefined ? task.finish : endOfDay(last)) : null,
    });
    if (!result.ok) return fail('PROGRESS_UPDATE_FAILED', result.error.message);
    next = result.project;

    const updated = next.tasks.find((candidate) => candidate.id === task.id)!;
    next = mapTask(next, task.id, (candidate) => ({
      ...candidate,
      workflowStatus: workflowStatusForPercent(candidate.workflowStatus, updated.percentComplete as number),
    }));
  }

  return { ok: true, project: rollUpSummaryProgress(next) };
}

// ---------------------------------------------------------------------------
// 直接指定進度
// ---------------------------------------------------------------------------

export interface SetTaskProgressInput {
  readonly taskId: TaskId;
  readonly percentComplete: Percent;
  readonly actualStart?: ISODateTime | null;
  readonly actualFinish?: ISODateTime | null;
  /** 省略則沿用任務目前的模式（預設 `'work'`）。 */
  readonly progressMode?: TaskProgressMode;
}

/**
 * 直接指定任務進度。`work` 模式下依比例改寫各指派的實際／剩餘工時
 * （等同在表格裡編輯 percentComplete）；`manual` 模式只改任務的進度，
 * 指派的實際工時仍由工時表決定，剩餘工時依指定進度換算。
 * 未指定 `actualStart` 時，pct > 0 取任務 start；pct = 100 時 `actualFinish` 取任務 finish。
 */
export function setTaskProgress(project: Project, input: SetTaskProgressInput): ProgressReportResult {
  const task = project.tasks.find((candidate) => candidate.id === input.taskId);
  if (task === undefined) return fail('TASK_NOT_FOUND', `Task ${input.taskId as string} does not exist.`);
  if (task.isSummary || project.tasks.some((candidate) => candidate.parentId === task.id)) {
    return fail('SUMMARY_TASK', 'Progress must be set on a leaf task.');
  }
  const pct = input.percentComplete as number;
  if (!Number.isFinite(pct) || pct < 0 || pct > 100) {
    return fail('INVALID_PERCENT', 'percentComplete must be between 0 and 100.');
  }

  const mode = input.progressMode ?? taskProgressMode(task);
  const actualStart = input.actualStart !== undefined
    ? input.actualStart
    : pct > 0 ? (task.actualStart ?? task.start) : null;
  const actualFinish = input.actualFinish !== undefined
    ? input.actualFinish
    : pct >= 100 ? (task.actualFinish ?? task.finish) : null;

  const taskAssignments = project.assignments.filter((assignment) => assignment.taskId === task.id);
  const totalWork = taskAssignments.reduce((sum, assignment) => sum + (assignment.work as number), 0);

  // 沒有工時可分攤（里程碑、無指派、指派工時全為 0）時，工時比例推導不成立：
  // 直接寫任務的進度與實際起迄日，指派維持原樣。
  if (mode === 'manual' || totalWork === 0) {
    const assignments = mode === 'manual'
      ? project.assignments.map((assignment) =>
        assignment.taskId === task.id
          ? { ...assignment, remainingWork: manualRemaining(assignment, pct), timephased: null }
          : assignment)
      : project.assignments;
    const tasks = project.tasks.map((candidate) => candidate.id === task.id ? {
      ...candidate,
      percentComplete: percent(pct),
      percentWorkComplete: percent(pct),
      actualStart: pct > 0 ? actualStart : null,
      actualFinish: pct >= 100 ? actualFinish : null,
      progressMode: mode,
      workflowStatus: workflowStatusForPercent(candidate.workflowStatus, pct),
    } : candidate);
    return { ok: true, project: rollUpSummaryProgress({ ...project, tasks, assignments }) };
  }

  const result = updateTaskProgress(project, {
    taskId: task.id,
    assignments: taskAssignments.map((assignment) => {
      const work = assignment.work as number;
      const actual = work * pct / 100;
      return {
        assignmentId: assignment.id,
        actualWork: minutes(actual),
        remainingWork: minutes(Math.max(0, work - actual)),
        overtimeWork: minutes(Math.min((assignment.overtimeWork ?? minutes(0)) as number, actual)),
      };
    }),
    actualStart: pct > 0 ? actualStart : null,
    actualFinish: pct >= 100 ? actualFinish : null,
  });
  if (!result.ok) return fail('PROGRESS_UPDATE_FAILED', result.error.message);
  return {
    ok: true,
    project: rollUpSummaryProgress(mapTask(result.project, task.id, (candidate) => ({
      ...candidate,
      progressMode: mode,
      workflowStatus: workflowStatusForPercent(candidate.workflowStatus, pct),
    }))),
  };
}

/**
 * 只切換任務的進度模式，不動進度、工時與日期。
 *
 * 與 `setTaskProgress({ progressMode })` 的差別：那支會依模式重算指派的
 * 實際／剩餘工時（它的語意是「指定進度」）。單純換模式時不該有這種副作用 ——
 * 例如從 manual 改回 work，實際工時應該維持工時表回填的結果，等下一次逐日
 * 回報再由引擎反推。
 */
export function setTaskProgressMode(
  project: Project,
  taskId: TaskId,
  mode: TaskProgressMode,
): ProgressReportResult {
  const task = project.tasks.find((candidate) => candidate.id === taskId);
  if (task === undefined) return fail('TASK_NOT_FOUND', `Task ${taskId as string} does not exist.`);
  if (task.isSummary || project.tasks.some((candidate) => candidate.parentId === task.id)) {
    return fail('SUMMARY_TASK', 'Progress mode must be set on a leaf task.');
  }
  if (taskProgressMode(task) === mode) return { ok: true, project };
  return { ok: true, project: mapTask(project, taskId, (candidate) => ({ ...candidate, progressMode: mode })) };
}

// ---------------------------------------------------------------------------
// 看板狀態
// ---------------------------------------------------------------------------

/**
 * 設定看板狀態。`done` 會把進度推到 100%（work 模式下剩餘工時歸零）；
 * 從 `done` 移回其他狀態不會竄改進度百分比 —— 只記錄顯式狀態，
 * 判讀一律走 `effectiveWorkflowStatus()`（pct 仍為 100 時它依舊回報 done）。
 */
export function setTaskWorkflowStatus(
  project: Project,
  taskId: TaskId,
  status: TaskWorkflowStatus,
): ProgressReportResult {
  const task = project.tasks.find((candidate) => candidate.id === taskId);
  if (task === undefined) return fail('TASK_NOT_FOUND', `Task ${taskId as string} does not exist.`);
  if (task.isSummary || project.tasks.some((candidate) => candidate.parentId === task.id)) {
    return fail('SUMMARY_TASK', 'Workflow status must be set on a leaf task.');
  }

  if (status !== 'done') {
    return { ok: true, project: mapTask(project, taskId, (candidate) => ({ ...candidate, workflowStatus: status })) };
  }
  const result = setTaskProgress(project, { taskId, percentComplete: percent(100) });
  if (!result.ok) return result;
  return { ok: true, project: mapTask(result.project, taskId, (candidate) => ({ ...candidate, workflowStatus: 'done' })) };
}

// ---------------------------------------------------------------------------
// 查詢
// ---------------------------------------------------------------------------

export interface ActualWorkEntry {
  readonly resourceId: ResourceId;
  readonly entryId: string;
  readonly date: ISODate;
  readonly taskId: TaskId;
  readonly assignmentId: AssignmentId | null;
  readonly actualWork: Minutes;
  readonly overtimeWork: Minutes;
  readonly remainingWork: Minutes | null;
  readonly notes: string;
  readonly reportedBy?: string;
  readonly reportedAt?: ISODateTime;
}

export interface ActualWorkQuery {
  readonly resourceId?: ResourceId;
  readonly taskId?: TaskId;
  /** 起日（含）。 */
  readonly from?: ISODate;
  /** 迄日（含）。 */
  readonly to?: ISODate;
}

/** 依資源／任務／日期區間查詢逐日實際工時，結果依日期、資源排序。 */
export function listActualWorkEntries(
  project: Project,
  query: ActualWorkQuery = {},
): readonly ActualWorkEntry[] {
  const rows: ActualWorkEntry[] = [];
  for (const sheet of project.timesheets ?? []) {
    if (query.resourceId !== undefined && sheet.resourceId !== query.resourceId) continue;
    for (const entry of sheet.entries) {
      if (query.taskId !== undefined && entry.taskId !== query.taskId) continue;
      if (query.from !== undefined && entry.date < query.from) continue;
      if (query.to !== undefined && entry.date > query.to) continue;
      rows.push({
        resourceId: sheet.resourceId,
        entryId: entry.id,
        date: entry.date,
        taskId: entry.taskId,
        assignmentId: entry.assignmentId,
        actualWork: entry.actualWork,
        overtimeWork: entry.overtimeWork,
        remainingWork: entry.remainingWork,
        notes: entry.notes,
        ...(entry.reportedBy === undefined ? {} : { reportedBy: entry.reportedBy }),
        ...(entry.reportedAt === undefined ? {} : { reportedAt: entry.reportedAt }),
      });
    }
  }
  return rows.sort((a, b) =>
    a.date < b.date ? -1 : a.date > b.date ? 1
      : (a.resourceId as string) < (b.resourceId as string) ? -1
        : (a.resourceId as string) > (b.resourceId as string) ? 1
          : a.entryId < b.entryId ? -1 : a.entryId > b.entryId ? 1 : 0);
}
